/**
 * @fileoverview Composable for chat window UI layout and positioning
 * @module composables/useChatWindowUI
 *
 * Manages chat window layout state, positioning, CSS classes,
 * and scroll behavior for the chat interface.
 *
 * @vue3migration - Phase 3D: Extracted from ChatWindow/Index.vue
 */
import { ref, computed, type ComputedRef, type Ref } from 'vue'

/**
 * Widget position from parent (App.vue drag functionality)
 */
interface WidgetPosition {
  left: number | null
  top: number | null
}

/**
 * Options for useChatWindowUI composable
 */
interface UseChatWindowUIOptions {
  /** Widget position from drag functionality */
  widgetPos: WidgetPosition
  /** Whether widget is being dragged */
  isWidgetReadyToDrag: boolean
  /** Settings from store */
  settings: ComputedRef<any>
  /** Whether UI beta mode is enabled */
  isUiBetaEnabled: ComputedRef<boolean>
  /** Whether device is mobile */
  isMobile: boolean
  /** Whether notification is enabled */
  isEnableNotification: ComputedRef<boolean>
  /** Live chat status */
  liveChatStatus: ComputedRef<boolean>
  /** Socket connection status */
  isConnected: ComputedRef<boolean>
  /** Tall view setting from kr_settings */
  tallViewSettings: boolean
}

/**
 * Composable for chat window UI layout and positioning
 *
 * @param options - Dependencies and configuration
 * @returns UI state and computed properties
 *
 * @example
 * ```ts
 * const {
 *   tallView,
 *   tall,
 *   headerClass,
 *   bannerClass,
 *   chatWindowRight,
 * } = useChatWindowUI({
 *   widgetPos,
 *   isWidgetReadyToDrag,
 *   settings: computed(() => store.settings),
 *   isUiBetaEnabled: computed(() => store.isUiBetaEnabled),
 *   isMobile: $device?.Mobile ?? false,
 *   isEnableNotification: computed(() => store.isEnabledNotification && Push.Permission.has()),
 *   liveChatStatus: computed(() => store.settings.liveChatStatus),
 *   isConnected: computed(() => store.isInitiating || store.socketConnectionStatus),
 *   tallViewSettings: window.kr_settings?.tallView ?? false,
 * })
 * ```
 */
export function useChatWindowUI(options: UseChatWindowUIOptions) {
  const {
    widgetPos,
    isWidgetReadyToDrag,
    settings,
    isUiBetaEnabled,
    isMobile,
    isEnableNotification,
    liveChatStatus,
    isConnected,
    tallViewSettings,
  } = options

  // ====================
  // State
  // ====================

  /** Whether tall/full-screen view is active (local state) */
  const tallView = ref(false)

  /** Whether scroll button should be shown */
  const isShowScrollButton = ref(false)

  /** Last scroll position for scroll direction detection */
  const lastScrollTop = ref(0)

  /** Whether header menu is open */
  const toShowHeaderMenu = ref(false)

  /** Chat input textarea height */
  const chatInputHeight = ref(38)

  /** Maintenance banner calculated height */
  const maintainBannerHeight = ref(0)

  /** Whether maintenance banner is visible */
  const isShowMaintenanceBanner = ref(true)

  // ====================
  // Computed - URL Parameters
  // ====================

  /**
   * Whether mobile mode is active (from URL query)
   */
  const isMobileMode = computed(() => {
    const params = new URLSearchParams(location.search)
    return params.get('mode') === 'mobile'
  })

  /**
   * Whether header should persist in mobile mode (from URL query)
   */
  const persistHeader = computed(() => {
    const params = new URLSearchParams(location.search)
    return params.get('header') === 'true'
  })

  /**
   * Mobile mode without header (default mobile behavior)
   */
  const isMobileModeDefault = computed(() => isMobileMode.value && !persistHeader.value)

  /**
   * Whether user-source exists in URL
   */
  const userSourceExist = computed(() => {
    const params = new URLSearchParams(location.search)
    return !!params.get('user-source')
  })

  // ====================
  // Computed - Layout
  // ====================

  /**
   * Tall view mode (getter/setter)
   * Combines kr_settings.tallView with local state
   */
  const tall = computed({
    get: () => tallViewSettings || tallView.value,
    set: (value: boolean) => {
      tallView.value = value
    },
  })

  /**
   * Content offset for positioning (accounts for header, banner)
   */
  const contentOffset = computed(() => {
    if (isMobileModeDefault.value) {
      return 0
    }
    if (showingNotifBanner.value) {
      return 60 + 24
    }
    return 60
  })

  /**
   * Whether notification banner should be shown
   */
  const showingNotifBanner = computed(() =>
    settings.value.enablePushNotification &&
    !isEnableNotification.value &&
    !isMobileMode.value
  )

  /**
   * Whether widget size section (expand/minimize buttons) should be shown
   */
  const isShowWidgetSizeSection = computed(() => {
    const isShowExpandButton = !isMobile && !tallViewSettings
    const isShowMinimizeButton = !isMobileMode.value
    return isShowExpandButton || isShowMinimizeButton
  })

  // ====================
  // Computed - CSS Classes
  // ====================

  /**
   * Header CSS classes based on mode and drag state
   */
  const headerClass = computed(() => {
    const classes: string[] = []

    // Apply themed colors only in desktop mode
    if (!isMobileModeDefault.value) {
      classes.push('header-themed')
    }

    // Mobile mode: hide header completely
    if (isMobileModeDefault.value) {
      classes.push('!h-0 !p-0 shadow-none !border-b-0')
    }

    // Drag cursor for UI Beta mode
    if (isUiBetaEnabled.value) {
      classes.push(isWidgetReadyToDrag ? 'cursor-grabbing' : 'cursor-grab')
    }

    return classes.join(' ')
  })

  /**
   * Banner CSS classes based on mode
   */
  const bannerClass = computed(() => {
    if (isMobileModeDefault.value) {
      return 'top-0 rounded-t-[10px] rounded-b-[4px]'
    }
    if (showingNotifBanner.value) {
      return 'top-[78px]'
    }
    return ''
  })

  /**
   * Main content area position style (as CSS variables for CSP compliance)
   */
  const mainPositionStyle = computed(() => ({
    '--panel-top': liveChatStatus.value
      ? `${contentOffset.value + 40}px`
      : `${contentOffset.value}px`,
  }))

  /**
   * Floating button position class
   */
  const floatingButtonClass = computed(() => {
    const topPosition =
      !isConnected.value || liveChatStatus.value ? 'top-[52px]' : 'top-3'
    return `absolute right-0 ${topPosition}`
  })

  // ====================
  // Computed - Window Positioning
  // ====================

  /**
   * Chat window right position
   */
  const chatWindowRight = computed(() => {
    if (tall.value) {
      return '0'
    } else if (widgetPos.left) {
      return 'auto'
    } else if (settings.value.size === 'large') {
      return '130'
    } else {
      return '100'
    }
  })

  /**
   * Chat window bottom position
   */
  const chatWindowBottom = computed(() => {
    if (tall.value) {
      return '0px'
    }
    // Returning blank string uses CSS value
    return ''
  })

  /**
   * Chat window height
   */
  const chatWindowHeight = computed(() => {
    if (tall.value) {
      return '100%'
    }
    // Returning blank string uses CSS value
    return ''
  })

  /**
   * Chat window top position
   */
  const chatWindowTop = computed(() => widgetPos.top || 'auto')

  /**
   * Chat window left position
   */
  const chatWindowLeft = computed(() => widgetPos.left || 'auto')

  /**
   * Chat input border radius CSS class (for CSP compliance)
   * Returns true for large radius (50px), false for small radius (10px)
   */
  const chatboxBorderLarge = computed(() => {
    if (!isUiBetaEnabled.value) {
      return false // 10px
    }
    return !(chatInputHeight.value > 50) // 50px when height <= 50, else 10px
  })

  /**
   * CSS variables for chat window positioning (CSP-compliant)
   * Used instead of inline styles for bottom, height, right, top, left
   */
  const chatWindowCssVars = computed(() => {
    const vars: Record<string, string> = {}

    // Bottom position
    if (tall.value) {
      vars['--widget-bottom'] = '0px'
    }
    // else use CSS default (leave undefined)

    // Height
    if (tall.value) {
      vars['--widget-height'] = '100%'
    }
    // else use CSS default

    // Right position (only when not RTL mode - handled in template class)
    if (tall.value) {
      vars['--widget-right'] = '0px'
    } else if (widgetPos.left) {
      vars['--widget-right'] = 'auto'
    } else if (settings.value.size === 'large') {
      vars['--widget-right'] = '130px'
    } else {
      vars['--widget-right'] = '100px'
    }

    // Top position (only when not tall)
    if (!tall.value) {
      const topVal = widgetPos.top || 'auto'
      vars['--widget-top'] = typeof topVal === 'number' ? `${topVal}px` : topVal
    }

    // Left position (only when not tall)
    if (!tall.value) {
      const leftVal = widgetPos.left || 'auto'
      vars['--widget-left'] = typeof leftVal === 'number' ? `${leftVal}px` : leftVal
    }

    return vars
  })

  // ====================
  // Methods
  // ====================

  /**
   * Open header menu dropdown
   */
  function openHeaderMenu() {
    toShowHeaderMenu.value = true
  }

  /**
   * Close header menu dropdown
   */
  function closeHeaderMenu() {
    toShowHeaderMenu.value = false
  }

  /**
   * Set maintenance banner height for layout calculations
   */
  function setMaintainBannerHeight() {
    const banner = document.getElementsByClassName('keyreply-maintenance-banner')
    if (banner.length > 0) {
      const bannerHeight = (banner[0] as HTMLElement).offsetHeight
      maintainBannerHeight.value = bannerHeight * 0.7
    }
  }

  /**
   * Close maintenance banner
   */
  function closeMaintenanceBanner() {
    isShowMaintenanceBanner.value = false
    maintainBannerHeight.value = 0
  }

  /**
   * Update scroll button visibility based on scroll direction
   *
   * @param scrollTop - Current scroll position
   * @param isScrollingDown - Whether scrolling down
   */
  function updateScrollButtonVisibility(scrollTop: number, isScrollingDown: boolean) {
    if (isScrollingDown) {
      isShowScrollButton.value = false
    }
    lastScrollTop.value = scrollTop
  }

  /**
   * Show scroll button
   */
  function showScrollButton() {
    isShowScrollButton.value = true
  }

  /**
   * Hide scroll button
   */
  function hideScrollButton() {
    isShowScrollButton.value = false
  }

  return {
    // State
    tallView,
    isShowScrollButton,
    lastScrollTop,
    toShowHeaderMenu,
    chatInputHeight,
    maintainBannerHeight,
    isShowMaintenanceBanner,

    // Computed - URL Parameters
    isMobileMode,
    persistHeader,
    isMobileModeDefault,
    userSourceExist,

    // Computed - Layout
    tall,
    contentOffset,
    showingNotifBanner,
    isShowWidgetSizeSection,

    // Computed - CSS Classes
    headerClass,
    bannerClass,
    mainPositionStyle,
    floatingButtonClass,

    // Computed - Window Positioning
    chatWindowRight,
    chatWindowBottom,
    chatWindowHeight,
    chatWindowTop,
    chatWindowLeft,
    chatboxBorderLarge,
    chatWindowCssVars,

    // Methods
    openHeaderMenu,
    closeHeaderMenu,
    setMaintainBannerHeight,
    closeMaintenanceBanner,
    updateScrollButtonVisibility,
    showScrollButton,
    hideScrollButton,
  }
}
